import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryUpdate,
} from "./_shared";

export type Project = Tables<"projects">;
export type ProjectInsert = Omit<TablesInsert<"projects">, "company_id">;
export type ProjectUpdate = TablesUpdate<"projects">;

type ProjectWithRelations = Project & {
  list_regions: { display_value: string } | null;
  manager_profile: { full_name: string | null } | null;
  project_segments: { count: number }[];
};

export type ProjectWithFullRelations = Project & {
  list_regions: { display_value: string } | null;
  manager_profile: { full_name: string | null } | null;
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
  project_segments: { count: number }[];
};

export type ProjectSegment = Tables<"project_segments">;
export type ProjectSegmentInsert = Omit<TablesInsert<"project_segments">, "company_id">;
export type ProjectSegmentUpdate = TablesUpdate<"project_segments">;

export interface ProjectSegmentWithRelations extends ProjectSegment {
  list_segments: { display_value: string } | null;
  segment_manager_profile: { full_name: string | null } | null;
}

export const projectService = {
  get(id: string) {
    return tryGet<Project>(`/api/projects/${encodeURIComponent(id)}`);
  },
  getWithRelations(id: string) {
    return tryGet<ProjectWithFullRelations>(
      `/api/projects/${encodeURIComponent(id)}?withRelations=true`,
    );
  },
  list(_companyId: string) {
    return tryList<Project>("/api/projects");
  },
  listWithRelations(_companyId: string) {
    return tryList<ProjectWithRelations>("/api/projects?withRelations=true");
  },
  create(data: ProjectInsert, _context: ServiceContext) {
    return tryCreate<Project>("/api/projects", data);
  },
  update(
    id: string,
    data: ProjectUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<Project>(`/api/projects/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/projects/${encodeURIComponent(id)}`);
  },
};

export const projectSegmentService = {
  listByProject(projectId: string) {
    return tryList<ProjectSegmentWithRelations>(
      `/api/projects/${encodeURIComponent(projectId)}/segments`,
    );
  },
  create(data: ProjectSegmentInsert, _context: ServiceContext) {
    return tryCreate<ProjectSegment>("/api/project-segments", data);
  },
  update(
    id: string,
    data: ProjectSegmentUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<ProjectSegment>(`/api/project-segments/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/project-segments/${encodeURIComponent(id)}`);
  },
};
